/* eslint-disable indent */
/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import Input from "../../common/Inputs/Input/Input";
import t from "../../../utils/common/translation/translator";
import {
  applyApplicationDataFilters,
  deleteAxis,
  saveApplicationDataDependencies,
  selectFiltersPopupOpened,
  selectLaunchSyncAppData,
  selectPossibleFiltersValues,
  setApplicationDataFilter,
  setCurrentAxisId,
  setCurrentFiltersStepperStep,
  setFiltersPopupOpened,
  setIsSynchronizingAppDataFilters,
  setNewAxisMode,
  setShowAxisPopup,
  selectCurrentAxisFilters,
  selectRetrofitConfiguration,
  updateAxisName
} from "../../../store/slices/retrofit";
import Spinner from "../../common/Icons/Spinner/Spinner";
import { formatConnectorType, formatMotorEncoder, formatNbPhases, formatShaftEnd } from "../Devices/Filters/utils";
import { logEvent, syncFiltersDependencies } from "../../../utils/common/api/api";
import FilterRecapChip from "./FilterRecapChip";
import { tData } from "../../../utils/common/translation/dataUtils";
import MsxIcons from "../../common/Icons/MsxIcons/MsxIcons";
import EditableAxisName from "./EditableAxisName";
import { selectConfigurationType, selectEventsData, updateConfiguration } from "../../../store/slices/common";

const AxisComponent = ({ axisId, setFiltersPopupActiveTab, minimal, newAxis, onMounted, checkInputs }) => {
  const dispatch = useDispatch();
  const configuration = useSelector(selectRetrofitConfiguration);
  const [axis, setAxis] = useState(null);
  const [editingMode, setEditingMode] = useState(false);
  const [data, setData] = useState(null);
  const applicationDataFiltersValues = useSelector(selectPossibleFiltersValues)?.applicationData;
  const [syncronizedFilters, setSyncronizedFilters] = useState(null);
  const [isInputEmpty, setIsInputEmpty] = useState(false);
  const axisFilters = useSelector(selectCurrentAxisFilters);

  const toNumber = (string) => {
    if (typeof string === "number") {
      return string;
    }
    const number = string?.replace(",", ".");
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(number) && number !== "") {
      return NaN;
    }
    return parseFloat(number);
  };

  const handleInputChange = (e, forceExec) => {
    const { name, value } = e.target;

    // eslint-disable-next-line no-restricted-globals
    if (forceExec || !(isNaN(toNumber(value)) && value !== "")) {
      const newData = { ...data, [name]: value };
      setData(newData);
      dispatch(setApplicationDataFilter({ axisId, applicationDataFilter: newData }));
    }
  };

  const blockDecimals = (e) => {
    // eslint-disable-next-line prefer-destructuring
    let value = e.target.value;
    const decimalSeparator = /[.,]/;
    if (decimalSeparator.test(value)) {
      const [integerPart, decimalPart] = value.split(decimalSeparator);
      if (decimalPart && decimalPart.length > 4) {
        return `${integerPart}.${decimalPart.slice(0, 4)}`;
      }
      value = `${integerPart}.${decimalPart}`;
    }
    return value;
  };

  useEffect(() => {
    if (onMounted) {
      onMounted(handleExecSyncFilters);
    }
  }, []);

  useEffect(() => {
    const axisFromConfiguration = configuration?.configData?.axes?.find((a) => a.id === axisId);
    setAxis(axisFromConfiguration);
    setData(axisFromConfiguration?.configFilters?.applicationDataFilter);
    if (axisFromConfiguration?.bundle?.motorReference) {
      setEditingMode(true);
    } else {
      setEditingMode(false);
    }
  }, [configuration]);

  const eventsData = useSelector(selectEventsData);

  const handleDeleteAxis = () => {
    logEvent(eventsData.locale, eventsData.types.deleteAxis, eventsData.tags.none, configuration?.configId);
    dispatch(deleteAxis(axis.id));
    dispatch(setNewAxisMode(false));
  };

  const handleApplyFiltersAndSelectBundle = async () => {
    if (
      !axis.configFilters?.applicationDataFilter?.nominalTorque ||
      !axis.configFilters?.applicationDataFilter?.nominalSpeed ||
      !isValidApplicationDataFilters() ||
      isSynchronizingFilters
    ) {
      setIsInputEmpty(true);
      return;
    }
    logEvent(eventsData.locale, eventsData.types.startSelection, eventsData.tags.none, configuration?.configId);
    await handleExecSyncFilters(true);
    dispatch(setCurrentFiltersStepperStep(1));
    // check every second if isSynchronizingFilters is false (max 30 seconds)
    const interval = setInterval(() => {
      if (!isSynchronizingFilters) {
        dispatch(setNewAxisMode(false));
        dispatch(applyApplicationDataFilters());
        dispatch(setCurrentAxisId(axis.id));
        dispatch(setShowAxisPopup(true));
        clearInterval(interval);
      }
    }, 500);
    setTimeout(() => {
      clearInterval(interval);
    }, 30000);
    dispatch(updateConfiguration());
  };

  // the filter that changed => launched the sync
  const [filterLaunchingSync, setFilterLaunchingSync] = useState(null);
  const configurationType = useSelector(selectConfigurationType);
  const {
    refetch: execSyncFilters,
    isFetching: isFetchingSyncFilters,
    isLoading: isLoadingSyncFilters
  } = useQuery(
    `Sync bundles filters of axis ${axisId}`,
    async () => {
      try {
        const axisFromConfiguration = configuration?.configData.axes.find((a) => a.id === axisId);
        // console.log("Sync filters of axis", axisFromConfiguration?.id);
        const appDataFilters = axisFromConfiguration.configFilters?.applicationDataFilter;
        const driveFilters = axisFromConfiguration.configFilters?.driveFilter;
        const motorFilters = axisFromConfiguration.configFilters?.motorFilter;
        const gearboxFilters = axisFromConfiguration.configFilters?.gearboxFilter;

        const motorEncoderFormatted = formatMotorEncoder(motorFilters?.motorEncoder);
        const shaftEndFormatted = formatShaftEnd(motorFilters?.shaftEnd);
        const connectorTypeFormatted = formatConnectorType(motorFilters?.connectorType);
        const nbPhasesFormatted = formatNbPhases(driveFilters?.nbPhases);

        const payload = {
          device: "ALL",
          criteria: {
            locale: window.msizerConfiguration?.locale || "en-WW",
            step: "bundle",
            applicationData: {
              nominalTorque: {
                min: toNumber(appDataFilters.nominalTorque),
                max: applicationDataFiltersValues?.nominalTorque.max
              },
              nominalSpeed: toNumber(appDataFilters.nominalSpeed),
              inertia: toNumber(appDataFilters.loadInertia),
              length: toNumber(appDataFilters.maxLength),
              withGearbox: motorFilters?.withGearbox,
              nominalTorqueOfMotor: motorFilters?.nominalTorque,
              nominalSpeedOfMotor: motorFilters?.nominalSpeed
            },
            drive: {
              type: driveFilters?.driveType || [],
              communicationInterfaces: driveFilters?.driveInterface || [],
              outputCurrents: driveFilters?.outputCurrent || [],
              nominalPower: driveFilters?.nominalPower || [],
              switchingFreq: driveFilters?.switchingFreq || [],
              format: driveFilters?.format || [],
              nbPhases: nbPhasesFormatted,
              driveVoltages: driveFilters?.driveVoltages || []
            },
            motor: {
              type: motorFilters?.motorType || [],
              feedbackType: motorEncoderFormatted,
              connectorType: connectorTypeFormatted,
              ipProtection: motorFilters?.ipDegreeOfProtection || [],
              holdingBrake: motorFilters?.holdingBrake || [],
              shaftEnd: shaftEndFormatted,
              length: motorFilters?.length,
              flangeSize: motorFilters?.flangeSize
            },
            gearbox: {
              productOrComponentType: motorFilters?.withGearbox ? gearboxFilters?.productOrComponentType || [] : [],
              reductionRatio: motorFilters?.withGearbox ? gearboxFilters?.ratio || [] : [],
              torsionalBacklash: data?.withGearbox ? gearboxFilters?.torsionalBacklash : undefined,
              externalDiameter: motorFilters?.withGearbox ? gearboxFilters?.externalDiameter || [] : []
            }
          }
        };

        const previousPayload = axisFromConfiguration?.axisState?.savedApplicationDataPayload;

        // check if the new payload is different from the previous one (to avoid useless calls)
        if (JSON.stringify(payload) !== JSON.stringify(previousPayload)) {
          const newFilters = await syncFiltersDependencies(configurationType, payload);

          const synchronizedFilters = {
            ...newFilters?.applicationData,
            ...filterLaunchingSync
          };

          setSyncronizedFilters(synchronizedFilters);

          dispatch(
            saveApplicationDataDependencies({
              axisId,
              dependencies: newFilters?.applicationData,
              appDataPayload: payload
            })
          );

          return newFilters;
        }
        // console.log("same payload");
        return null;
      } catch (error) {
        console.error("ERR", error.response?.status);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  const [savedState, setSavedState] = useState({
    nominalTorque: data?.nominalTorque || "",
    nominalSpeed: data?.nominalSpeed || "",
    maxLength: data?.maxLength || ""
  });

  const isValidApplicationDataFilters = () =>
    (!data?.nominalTorque ||
      (toNumber(data?.nominalTorque) >= applicationDataFiltersValues?.nominalTorque.min &&
        toNumber(data?.nominalTorque) <=
          (storedApplicationDataDependencies?.nominalTorque?.max ||
            syncronizedFilters?.nominalTorque?.max ||
            applicationDataFiltersValues?.nominalTorque.max))) &&
    (!data?.nominalSpeed ||
      (toNumber(data?.nominalSpeed) >= 0 &&
        toNumber(data?.nominalSpeed) <=
          (storedApplicationDataDependencies?.nominalSpeed?.max ||
            syncronizedFilters?.nominalSpeed?.max ||
            applicationDataFiltersValues?.nominalSpeed.max))) &&
    (!toNumber(data?.maxLength) ||
      toNumber(data?.maxLength) >=
        (storedApplicationDataDependencies?.length?.min ||
          syncronizedFilters?.length?.min ||
          applicationDataFiltersValues?.length.min));

  const handleExecSyncFilters = async (forceExec = false) => {
    if (
      forceExec ||
      (isValidApplicationDataFilters() &&
        (savedState.nominalTorque !== data?.nominalTorque ||
          savedState.nominalSpeed !== data?.nominalSpeed ||
          savedState.maxLength !== data?.maxLength)) ||
      (!data && !storedApplicationDataDependencies)
    ) {
      setSavedState({
        nominalTorque: data?.nominalTorque || "",
        nominalSpeed: data?.nominalSpeed || "",
        maxLength: data?.maxLength || ""
      });

      const newFilters = await execSyncFilters();

      return newFilters;
    }
    return null;
  };

  const launchSyncAppDataRedux = useSelector(selectLaunchSyncAppData);

  const handleExecSyncFiltersMemoized = useMemo(
    () => () => {
      handleExecSyncFilters(true);
    },
    [launchSyncAppDataRedux]
  );

  useEffect(() => {
    handleExecSyncFiltersMemoized();
  }, [launchSyncAppDataRedux]);

  const [isSynchronizingFilters, setIsSynchronizingFilters] = useState(false);

  useEffect(() => {
    setIsSynchronizingFilters(isFetchingSyncFilters || isLoadingSyncFilters);
    dispatch(setIsSynchronizingAppDataFilters(isFetchingSyncFilters || isLoadingSyncFilters));
  }, [isFetchingSyncFilters, isLoadingSyncFilters]);

  const storedApplicationDataDependencies = useSelector(
    (state) => state.retrofit.configuration?.configData?.axes?.find((a) => a.id === axisId)?.axisState?.dependencies
  );

  useEffect(() => {
    // console.log(axisId);
    handleExecSyncFilters(true);
  }, []);

  if (newAxis) {
    useEffect(() => {
      dispatch(setNewAxisMode(true));
    }, []);
  }

  const filtersPopupOpened = useSelector(selectFiltersPopupOpened);

  useEffect(() => {
    if (!filtersPopupOpened) {
      handleExecSyncFilters(true);
    }
  }, [filtersPopupOpened]);

  const [openingFiltersPopup, setOpeningFiltersPopup] = useState(false);

  const openFiltersPopup = async () => {
    setOpeningFiltersPopup(true);
    await handleExecSyncFilters(true);
    dispatch(setCurrentAxisId(axis.id));
    dispatch(setFiltersPopupOpened(true));
    setOpeningFiltersPopup(false);
    logEvent(eventsData.locale, eventsData.types.editMachineSettings, eventsData.tags.none, configuration?.configId);
  };

  const focusNextInput = (currentInput, allInputs, isNext) => {
    const currentIndex = Array.from(allInputs).indexOf(currentInput);
    const nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;
    const nextInput = allInputs[nextIndex];

    if (nextInput && allInputs.length > nextIndex && nextIndex >= 0) {
      nextInput.focus();
    } else {
      currentInput.blur();
    }
  };

  const preventDefaultAndStopPropagation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    e.nativeEvent.preventDefault();
    e.nativeEvent.stopPropagation();
    e.nativeEvent.returnValue = false;
    e.nativeEvent.cancelBubble = true;
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      preventDefaultAndStopPropagation(e);

      const allInputs = document.querySelectorAll("input");
      const isNext = e.key === "ArrowRight"; // Set isNext based on the arrow key pressed
      focusNextInput(e.target, allInputs, isNext);
    } else if (e.key === "Enter") {
      if (e.ctrlKey) {
        preventDefaultAndStopPropagation(e);
        if (!minimal) {
          handleApplyFiltersAndSelectBundle();
        }
      } else {
        preventDefaultAndStopPropagation(e);

        const allInputs = document.querySelectorAll("input");
        // Always focus the next input when Enter is pressed
        focusNextInput(e.target, allInputs, true);
      }
    }
  };

  const computeNominalTorqueIsInvalid =
    axis?.configFilters?.applicationDataFilter?.nominalTorque < applicationDataFiltersValues?.nominalTorque.min ||
    axis?.configFilters?.applicationDataFilter?.nominalTorque >
      (storedApplicationDataDependencies?.nominalTorque?.max ||
        syncronizedFilters?.nominalTorque?.max ||
        applicationDataFiltersValues?.nominalTorque.max) ||
    (isInputEmpty && axis.configFilters?.applicationDataFilter?.nominalTorque === "");

  const computeNominalSpeedIsInvalid =
    axis?.configFilters?.applicationDataFilter?.nominalSpeed < 0 ||
    axis?.configFilters?.applicationDataFilter?.nominalSpeed >
      (storedApplicationDataDependencies?.nominalSpeed?.max ||
        syncronizedFilters?.nominalSpeed?.max ||
        applicationDataFiltersValues?.nominalSpeed.max) ||
    (isInputEmpty && axis.configFilters?.applicationDataFilter?.nominalSpeed === "");

  const computeLoadInertiaIsInvalid =
    (axis?.configFilters?.applicationDataFilter?.loadInertia &&
      axis?.configFilters?.applicationDataFilter?.loadInertia <
        (syncronizedFilters?.inertia?.min || applicationDataFiltersValues?.inertia.min)) ||
    axis?.configFilters?.applicationDataFilter?.loadInertia >
      (syncronizedFilters?.inertia?.max || applicationDataFiltersValues?.inertia.max);

  const computeMaxLengthIsInvalid =
    axis?.configFilters?.applicationDataFilter?.maxLength &&
    axis?.configFilters?.applicationDataFilter?.maxLength <
      (storedApplicationDataDependencies?.length?.min ||
        syncronizedFilters?.length?.min ||
        applicationDataFiltersValues?.length.min);

  const handleUpdateAxisName = (newName) => {
    if (!newName) {
      return;
    }
    dispatch(updateAxisName({ axisId: axis.id, name: newName }));
    dispatch(updateConfiguration());
  };

  return (
    axis && (
      <div
        data-testid="axis-component"
        className={`md:border-[1px] md:px-2 md:pb-3 md:bg-gray-5 md:border-l-4 md:shadow-sm md:overflow-auto py-3 space-y-3 transition-all duration-75 md:border-solid md:border-gray-200
        ${editingMode ? "border-l-primary" : "border-l-gray-300 hover:border-l-gray-400"}
        ${openingFiltersPopup && "opacity-80"}
        `}
      >
        <button
          type="button"
          className={`${
            minimal || configuration?.configData.axes.length <= 1 ? "hidden" : "flex"
          } md:hidden mt-2 pt-1 w-full flex justify-end`}
        >
          <MsxIcons
            onClick={() => {
              handleDeleteAxis();
            }}
            name="close"
            className="text-lg text-gray-400"
          />
        </button>
        <div className="md:hidden flex justify-end space-x-2">
          <FilterRecapChip
            onClick={() => {
              setFiltersPopupActiveTab(1);
              openFiltersPopup();
            }}
            value={tData("drive", "nb_phases", axis.configFilters?.driveFilter?.nbPhases?.[0])}
          />
          <FilterRecapChip
            onClick={() => {
              setFiltersPopupActiveTab(1);
              openFiltersPopup();
            }}
            value={axis.configFilters?.driveFilter?.driveVoltages?.[0]}
          />
          <FilterRecapChip
            onClick={() => {
              setFiltersPopupActiveTab(2);
              openFiltersPopup();
            }}
            value={axis.configFilters?.driveFilter?.driveInterface?.[0]}
          />
        </div>
        <div className="flex space-x-10">
          <div className="max-w-[50%]">
            <EditableAxisName
              axisName={axis.name}
              onConfirm={(newName) => {
                handleUpdateAxisName(newName);
              }}
              configId={configuration?.configId}
            />
          </div>
          <div className="hidden md:flex md:visible space-x-2">
            <FilterRecapChip
              onClick={() => {
                openFiltersPopup();
              }}
              value={tData("drive", "nb_phases", axis.configFilters?.driveFilter?.nbPhases?.[0])}
            />
            <FilterRecapChip
              onClick={() => {
                openFiltersPopup();
              }}
              value={axis.configFilters?.driveFilter?.driveVoltages?.[0]}
            />
            <FilterRecapChip
              onClick={() => {
                openFiltersPopup();
              }}
              value={axis.configFilters?.driveFilter?.driveInterface?.[0]}
            />
          </div>
        </div>
        {minimal && (
          <div className="flex items-center">
            <div className="py-1 px-4 border-[1px] border-gray-300 bg-white">Application data</div>
            <div className="flex-1 h-px bg-gray-300" />
            <div className="w-px h-2 mt-2 bg-gray-300" />
          </div>
        )}

        <div className="flex flex-col md:flex-row md:space-x-5 space-y-3">
          <div className="flex-1 flex items-center md:space-x-5 space-y-3 relative flex-col flex-wrap md:flex-row">
            <div
              className={`bg-gray-50 rounded backdrop-blur-xs bg-opacity-50 h-full w-full top-0 left-0 absolute flex items-center justify-center z-20 ${
                !isSynchronizingFilters && "hidden"
              }`}
            >
              <Spinner small />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full md:w-fit">
              <Input
                data-testid="input-nominalTorque"
                // type="number"
                type="text"
                pattern="^\d+(\.\d{1,2})?$"
                autoComplete="off"
                required
                min={0}
                max={
                  storedApplicationDataDependencies?.nominalTorque?.max ||
                  syncronizedFilters?.nominalTorque?.max ||
                  applicationDataFiltersValues?.nominalTorque.max
                }
                isInvalid={
                  computeNominalTorqueIsInvalid || (checkInputs && !axisFilters?.applicationDataFilter?.nominalTorque)
                }
                invalidText={
                  (isInputEmpty && axis.configFilters?.applicationDataFilter?.nominalTorque === "") ||
                  (checkInputs && !axisFilters?.applicationDataFilter?.nominalTorque)
                    ? t("msizer.ui.mandatory_field_missing")
                    : t("msizer.ui.field_out_range")
                }
                onBlur={() => {
                  // If the value is invalid, we reset it to undefined
                  if (computeNominalTorqueIsInvalid) {
                    handleInputChange(
                      {
                        target: { value: "", name: "nominalTorque" }
                      },
                      true
                    );
                  }
                }}
                onChange={(e) => {
                  if (isSynchronizingFilters) return;
                  const value = blockDecimals(e);
                  handleInputChange({
                    target: { value, name: "nominalTorque" }
                  });
                  setFilterLaunchingSync({
                    nominalTorque: {
                      min: 0,
                      max: syncronizedFilters?.nominalTorque?.max || applicationDataFiltersValues?.nominalTorque.max
                    }
                  });
                }}
                // onBlur={() => {
                //   handleExecSyncFilters();
                // }}
                onFocus={() => {
                  // console.log("setCurrentAxisId", axis.id);
                  dispatch(setCurrentAxisId(axis.id));
                  handleExecSyncFilters();
                }}
                name="nominalTorque"
                value={axis.configFilters?.applicationDataFilter?.nominalTorque}
                label={`${t("msizer.ui.nominal_torque")} *`}
                unit={t("msizer.ui.unit.nm")}
                tooltip={t("msizer.ui.specify.nominal_torque_tooltip")}
                tooltipId={`nominalTorque-${axisId}`}
                // on click on Enter key or right arrow => trigger the shift key
                onKeyDown={(e) => {
                  handleInputKeyDown(e);
                }}
              />
              <Input
                data-testid="input-nominalSpeed"
                type="text"
                pattern="^\d+(\.\d{1,2})?$"
                autoComplete="off"
                required
                // min={applicationDataFiltersValues?.nominalSpeed.min}
                min={0}
                max={
                  storedApplicationDataDependencies?.nominalSpeed?.max ||
                  syncronizedFilters?.nominalSpeed?.max ||
                  applicationDataFiltersValues?.nominalSpeed.max
                }
                isInvalid={
                  computeNominalSpeedIsInvalid || (checkInputs && !axisFilters?.applicationDataFilter?.nominalSpeed)
                }
                invalidText={
                  (isInputEmpty && axis.configFilters?.applicationDataFilter?.nominalSpeed === "") ||
                  (checkInputs && !axisFilters?.applicationDataFilter?.nominalSpeed)
                    ? t("msizer.ui.mandatory_field_missing")
                    : t("msizer.ui.field_out_range")
                }
                onBlur={() => {
                  // If the value is invalid, we reset it to undefined
                  if (computeNominalSpeedIsInvalid) {
                    handleInputChange(
                      {
                        target: { value: "", name: "nominalSpeed" }
                      },
                      true
                    );
                  }
                }}
                onChange={(e) => {
                  if (isSynchronizingFilters) return;
                  const value = blockDecimals(e);
                  handleInputChange({
                    target: { value, name: "nominalSpeed" }
                  });
                  setFilterLaunchingSync({
                    nominalSpeed: {
                      min: 0,
                      max: syncronizedFilters?.nominalSpeed?.max || applicationDataFiltersValues?.nominalSpeed.max
                    }
                  });
                }}
                // onBlur={() => {
                //   handleExecSyncFilters();
                // }}
                onFocus={() => {
                  // console.log("setCurrentAxisId", axis.id);
                  dispatch(setCurrentAxisId(axis.id));
                  handleExecSyncFilters();
                }}
                name="nominalSpeed"
                value={axis.configFilters?.applicationDataFilter?.nominalSpeed}
                label={`${t("msizer.ui.nominal_speed")} *`}
                unit={t("msizer.ui.unit.rpm")}
                tooltip={t("msizer.ui.specify.nominal_speed_tooltip")}
                tooltipId={`nominalSpeed-${axisId}`}
                onKeyDown={(e) => {
                  handleInputKeyDown(e);
                }}
              />
              <Input
                data-testid="input-loadInertia"
                type="text"
                recommended
                pattern="^\d+(\.\d{1,2})?$"
                autoComplete="off"
                // min={
                //   syncronizedFilters?.inertia?.min ||
                //   applicationDataFiltersValues?.inertia.min
                // }
                // max={
                //   syncronizedFilters?.inertia?.max ||
                //   applicationDataFiltersValues?.inertia.max
                // }
                isInvalid={computeLoadInertiaIsInvalid}
                invalidText={t("msizer.ui.field_out_range")}
                onBlur={() => {
                  // If the value is invalid, we reset it to undefined
                  if (computeLoadInertiaIsInvalid) {
                    handleInputChange(
                      {
                        target: { value: "", name: "loadInertia" }
                      },
                      true
                    );
                  }
                }}
                onChange={(e) => {
                  if (isSynchronizingFilters) return;
                  const value = blockDecimals(e);
                  handleInputChange({ target: { value, name: "loadInertia" } });
                }}
                // onBlur={() => {
                //   handleExecSyncFilters();
                // }}
                onFocus={() => {
                  // console.log("setCurrentAxisId", axis.id);
                  dispatch(setCurrentAxisId(axis.id));
                  handleExecSyncFilters();
                }}
                value={axis.configFilters?.applicationDataFilter?.loadInertia}
                name="loadInertia"
                label={t("msizer.ui.load_inertia")}
                unit={t("msizer.ui.unit.kg_cm_squared")}
                tooltip={t("msizer.ui.specify.load_inertia_tooltip")}
                tooltipId={`loadInertia-${axisId}`}
                onKeyDown={(e) => {
                  handleInputKeyDown(e);
                }}
              />
              <Input
                data-testid="input-maxLength"
                type="text"
                pattern="^\d+(\.\d{1,2})?$"
                autoComplete="off"
                min={
                  storedApplicationDataDependencies?.length?.min ||
                  syncronizedFilters?.length?.min ||
                  applicationDataFiltersValues?.length.min
                }
                isInvalid={computeMaxLengthIsInvalid}
                invalidText={t("msizer.ui.field_out_range")}
                onBlur={() => {
                  // If the value is invalid, we reset it to undefined
                  if (computeMaxLengthIsInvalid) {
                    handleInputChange(
                      {
                        target: { value: "", name: "maxLength" }
                      },
                      true
                    );
                  }
                }}
                onChange={(e) => {
                  if (isSynchronizingFilters) return;
                  const value = blockDecimals(e);
                  handleInputChange({ target: { value, name: "maxLength" } });
                  setFilterLaunchingSync({
                    maxLength: {
                      min: syncronizedFilters?.length?.min || applicationDataFiltersValues?.length.min
                    }
                  });
                }}
                // onBlur={() => {
                //   handleExecSyncFilters();
                // }}
                onFocus={() => {
                  // console.log("setCurrentAxisId", axis.id);
                  dispatch(setCurrentAxisId(axis.id));
                  handleExecSyncFilters();
                }}
                value={axis.configFilters?.applicationDataFilter?.maxLength}
                name="maxLength"
                label={t("msizer.ui.max_length")}
                unit={t("msizer.ui.unit.mm")}
                tooltip={t("msizer.ui.specify.max_length_tooltip")}
                tooltipId={`maxLength-${axisId}`}
                onKeyDown={(e) => {
                  handleInputKeyDown(e);
                }}
              />
            </div>
          </div>
          {/* {filtersApplied && <div>hello</div>} */}
          {!minimal && (
            <div className="flex items-end">
              <div
                className={`flex space-x-2 w-full mr-7 md:mr-0 ${
                  computeMaxLengthIsInvalid ||
                  computeLoadInertiaIsInvalid ||
                  computeNominalSpeedIsInvalid ||
                  computeNominalTorqueIsInvalid
                    ? "mb-4"
                    : ""
                }`}
              >
                <qds-button
                  size="small"
                  importance="emphasized"
                  id={`specify-button-apply-${axisId}`}
                  onClick={handleApplyFiltersAndSelectBundle}
                  disabled={
                    !axis.configFilters?.applicationDataFilter?.nominalTorque ||
                    !axis.configFilters?.applicationDataFilter?.nominalSpeed ||
                    !isValidApplicationDataFilters() ||
                    isSynchronizingFilters
                  }
                  text={t("msizer.ui.select.table.apply_and_select")}
                ></qds-button>

                <qds-button
                  id={`specify-button-delete-${axisId}`}
                  title={t("msizer.ui.tooltip.delete_axis")}
                  disabled={configuration?.configData.axes.length <= 1}
                  onClick={handleDeleteAxis}
                  icon-name="trash-can"
                  size="small"
                ></qds-button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  );
};

AxisComponent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  axisId: PropTypes.any.isRequired,
  setFiltersPopupActiveTab: PropTypes.func,
  minimal: PropTypes.bool,
  newAxis: PropTypes.bool,
  onMounted: PropTypes.func,
  checkInputs: PropTypes.bool
};

AxisComponent.defaultProps = {
  setFiltersPopupActiveTab: () => {},
  minimal: false,
  newAxis: false,
  onMounted: null,
  checkInputs: false
};

export default AxisComponent;
